import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Search performance — the SEO module's first real screen (S-B22a).
 *
 * Two things at once, and they belong together for a reason a reviewer of the
 * Google submission would recognise: **the property picker and the figures it
 * produces**. Search Console has two kinds of property holding different data —
 * `sc-domain:example.com` covers every scheme and subdomain,
 * `https://www.example.com/` covers exactly that prefix — so AugRank's match is a
 * ranked guess and the tenant is entitled to correct it. Putting the correction
 * on a settings page far from the numbers it changes would mean somebody staring
 * at figures that look wrong with no idea what to do about them.
 *
 * `docs/GOOGLE-OAUTH-VERIFICATION.md` §5 shot 5 is this screen: the picker
 * listing real properties, and clicks / impressions / CTR / average position with
 * the query table underneath.
 *
 * **The empty state is a real state and appears often.** A property registered
 * last week genuinely has no data, and Search Console's own figures lag by two to
 * three days. Rendering four zeroes would be a lie dressed as a measurement, so
 * `has_data` drives a sentence that says what is actually happening.
 */
export function SearchPerformance(): JSX.Element {
  const toast = useToast()
  const [siteId, setSiteId] = useState<string | null>(null)
  const [picking, setPicking] = useState(false)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])

  // The first site until somebody chooses another. Derived rather than stored in
  // an effect: an effect that set state from a fetch would render once with no
  // selection and once with one, and the second render would refetch.
  const listed = sites.state.data?.sites ?? []
  const selectedId = siteId ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null
  const hasProperty = site !== null && site.gsc_property !== null && site.gsc_property !== ''

  const performance = useQuery(
    (signal) =>
      selectedId === null || !hasProperty
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/performance', {
              params: { path: { site_id: selectedId } },
              signal,
            }),
          ),
    [selectedId, hasProperty],
  )

  return (
    <>
      <div className="ar-row">
        <h1 className="ar-h1">Search performance</h1>
        <span className="app-spacer" />
        {listed.length > 1 ? (
          <select
            className="ar-select"
            aria-label="Which site"
            value={selectedId ?? ''}
            onChange={(event) => {
              setSiteId(event.target.value)
              setPicking(false)
            }}
          >
            {listed.map((row) => (
              <option key={row.id} value={row.id}>
                {row.domain}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {sites.state.status === 'loading' ? (
        <Loading label="Loading your sites" />
      ) : sites.state.status === 'error' ? (
        <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
      ) : site === null ? (
        <Empty
          title="No site yet"
          body="Add your website and AugRank can start reading how it performs in Google search."
          action={<Link to="/onboarding/site">Add your site</Link>}
        />
      ) : (
        <div className="ar-stack">
          <section className="ar-card">
            <div className="ar-card__header">
              <h3>{site.domain}</h3>
              <span className="ar-card__spacer" />
              {hasProperty ? (
                <span className="ar-mono ar-text-body-sm">{site.gsc_property}</span>
              ) : (
                <span className="ar-badge ar-badge--warning">No property chosen</span>
              )}
            </div>

            {/*
              `.ar-card__body` carries the inset. Without it the prose starts at
              the card's border while the heading above it starts a
              `--ar-space-6` in, and the two disagree about where the card's
              left edge is — which is what made this screen read as misaligned.
            */}
            <div className="ar-card__body">
              {hasProperty ? (
                <p className="ar-text-body-sm ar-muted">
                  Reading the Search Console property above.{' '}
                  {site.verified_at !== null && site.verified_at !== undefined
                    ? `Last confirmed ${formatDate(site.verified_at)}.`
                    : null}{' '}
                  <button
                    type="button"
                    className="ar-link"
                    onClick={() => {
                      setPicking((open) => !open)
                    }}
                  >
                    {picking ? 'Cancel' : 'Not the right one?'}
                  </button>
                </p>
              ) : (
                <>
                  <p className="ar-empty ar-empty--inline">
                    AugRank could not match this site to a Search Console property in the Google
                    account you connected. Pick the right one, or connect a different account.
                  </p>
                  <div className="ar-row" style={{ gap: 'var(--ar-space-3)' }}>
                    <Button
                      onClick={() => {
                        setPicking(true)
                      }}
                    >
                      Choose a property
                    </Button>
                    <Link to="/settings/connections">Connect a different Google account</Link>
                  </div>
                </>
              )}

              {picking ? (
                <PropertyPicker
                  siteId={site.id}
                  current={site.gsc_property ?? null}
                  onChosen={() => {
                    setPicking(false)
                    sites.refetch()
                    toast.show({
                      kind: 'success',
                      title: 'Property updated',
                      body: `${site.domain} now reads from the property you chose.`,
                    })
                  }}
                />
              ) : null}
            </div>
          </section>

          {!hasProperty ? null : performance.state.status === 'loading' ? (
            <Loading label="Reading Search Console" />
          ) : performance.state.status === 'error' ? (
            <ErrorState failure={performance.state.failure} onRetry={performance.refetch} />
          ) : performance.state.data === null ? null : performance.state.data.has_data ? (
            <Figures data={performance.state.data} />
          ) : (
            <Empty
              title="Google has no data for this property yet"
              body={
                'Search Console needs a few days of traffic before it reports anything, and its ' +
                'figures always lag by two to three days. Nothing is wrong — check back later ' +
                'this week.'
              }
            />
          )}

          <TrafficPanel
            siteId={site.id}
            domain={site.domain}
            propertyId={site.ga4_property_id ?? null}
            onChosen={() => {
              sites.refetch()
              toast.show({
                kind: 'success',
                title: 'Analytics property updated',
                body: `${site.domain} now reads traffic from the property you chose.`,
              })
            }}
          />
        </div>
      )}
    </>
  )
}

/**
 * Google Analytics, beside the search figures. Shot 7 of the demonstration video.
 *
 * **Below the Search Console figures rather than on its own screen**, and that
 * placement is the feature. §5's narration is *"so the customer can see whether a
 * page that gained impressions also gained visits"* — a question nobody can answer
 * by navigating between two tabs. Impressions rising while sessions do not is the
 * single most useful thing this product can show a small business, and it is only
 * visible when both are in one viewport.
 *
 * **Absent, not broken, when Analytics was not granted.** Google's granular
 * consent renders one tick box per scope, so a tenant who took Search Console and
 * declined Analytics made a choice. The API answers 409 with a sentence naming
 * that permission, and this renders it as a quiet offer to reconnect rather than
 * as an error — the same treatment the DNS-token route gets under partial consent.
 */
function TrafficPanel({
  siteId,
  domain,
  propertyId,
  onChosen,
}: {
  siteId: string
  domain: string
  propertyId: string | null
  onChosen: () => void
}): JSX.Element {
  const [picking, setPicking] = useState(false)
  const chosen = propertyId !== null && propertyId !== ''

  const traffic = useQuery(
    (signal) =>
      !chosen
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/traffic', {
              params: { path: { site_id: siteId } },
              signal,
            }),
          ),
    [siteId, chosen],
  )

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h3>Traffic</h3>
        <span className="ar-card__spacer" />
        {chosen ? (
          <span className="ar-mono ar-text-body-sm">Analytics property {propertyId}</span>
        ) : (
          <span className="ar-badge ar-badge--warning">No Analytics property chosen</span>
        )}
      </div>

      <div className="ar-card__body">
        {!chosen ? (
          <>
            <p className="ar-empty ar-empty--inline">
              Connect a Google Analytics property and you will see whether the pages gaining
              impressions above are actually gaining visits.
            </p>
            <Button
              onClick={() => {
                setPicking(true)
              }}
            >
              Choose an Analytics property
            </Button>
          </>
        ) : (
          <p className="ar-text-body-sm ar-muted">
            Reading Google Analytics.{' '}
            <button
              type="button"
              className="ar-link"
              onClick={() => {
                setPicking((open) => !open)
              }}
            >
              {picking ? 'Cancel' : 'Not the right one?'}
            </button>
          </p>
        )}

        {picking ? (
          <Ga4Picker
            siteId={siteId}
            domain={domain}
            current={propertyId}
            onChosen={() => {
              setPicking(false)
              onChosen()
            }}
          />
        ) : null}

        {!chosen ? null : traffic.state.status === 'loading' ? (
          <Loading label="Reading Google Analytics" />
        ) : traffic.state.status === 'error' ? (
          <ErrorState failure={traffic.state.failure} onRetry={traffic.refetch} />
        ) : traffic.state.data === null ? null : traffic.state.data.has_data ? (
          <>
            <div className="app-stats">
              <Stat label="Sessions" value={traffic.state.data.sessions.toLocaleString('en-IN')} />
              <Stat
                label="Engaged sessions"
                value={traffic.state.data.engaged_sessions.toLocaleString('en-IN')}
              />
              <Stat label="Users" value={traffic.state.data.total_users.toLocaleString('en-IN')} />
              <Stat
                label="Key events"
                value={traffic.state.data.key_events.toLocaleString('en-IN')}
              />
            </div>
            <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
              {formatDate(traffic.state.data.start_date)} —{' '}
              {formatDate(traffic.state.data.end_date)}, straight from Google Analytics.{' '}
              {(traffic.state.data.engagement_rate * 100).toFixed(0)}% of sessions were engaged.
              Read-only — AugRank never writes to Analytics.
            </p>
          </>
        ) : (
          <Empty
            title="Google Analytics has no data for this property yet"
            body={
              'Either the property was created recently, or its tag is not installed on the site. ' +
              'Nothing is wrong with the connection — Analytics simply has nothing to report yet.'
            }
          />
        )}
      </div>
    </section>
  )
}

/**
 * The GA4 picker. Fetched only when opened, because it is a live call to Google.
 *
 * **The tenant never types an id, and that is deliberate.** GA4 exposes two
 * identifiers: `G-XXXXXXXXXX` is the measurement id their tracking snippet
 * carries and the one they will reach for, while the API addresses a property by
 * its numeric id. They are not convertible, so a text box would collect the wrong
 * one from most people and fail with a 404 that explains nothing.
 */
function Ga4Picker({
  siteId,
  domain,
  current,
  onChosen,
}: {
  siteId: string
  domain: string
  current: string | null
  onChosen: () => void
}): JSX.Element {
  const properties = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/analytics-properties', {
          params: { path: { site_id: siteId } },
          signal,
        }),
      ),
    [siteId],
  )
  const choose = useMutation((propertyId: string) =>
    call(() =>
      api.PUT('/api/v1/seo/sites/{site_id}/analytics-property', {
        params: { path: { site_id: siteId } },
        body: { property_id: propertyId },
      }),
    ),
  )

  if (properties.state.status === 'loading') return <Loading label="Asking Google" />
  if (properties.state.status === 'error') {
    return <ErrorState failure={properties.state.failure} onRetry={properties.refetch} />
  }

  const listed = properties.state.data.properties
  if (listed.length === 0) {
    return (
      <Empty
        title="This Google account owns no Analytics properties"
        body={`Sign in with the account that has Analytics for ${domain}, or ask whoever manages it to add you in Google Analytics.`}
        action={<Link to="/settings/connections">Connect a different account</Link>}
      />
    )
  }

  return (
    <div className="ar-panel ar-stack ar-stack--tight">
      <InlineError failure={choose.state.failure} />
      <strong>Which Analytics property is this site?</strong>
      <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
        A GA4 property is named by whoever set it up, so it may not mention {domain} at all. The
        account it belongs to is shown underneath to tell two similar names apart.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {listed.map((property) => (
          <li key={property.property_id} className="app-conn">
            <span className="app-conn__body">
              <span>
                {property.display_name === '' ? property.property_id : property.display_name}
              </span>
              <span className="ar-text-body-sm ar-muted">
                {property.account_display_name} ·{' '}
                <span className="ar-mono">{property.property_id}</span>
                {property.reportable ? null : ' · this property type reports no data'}
              </span>
            </span>
            <Button
              size="sm"
              tone={property.property_id === current ? 'ghost' : 'primary'}
              disabled={!property.reportable || property.property_id === current}
              busy={choose.pending}
              busyLabel="Saving"
              onClick={() => {
                void (async () => {
                  const result = await choose.run(property.property_id)
                  if (result.ok) onChosen()
                })()
              }}
            >
              {property.property_id === current ? 'Current' : 'Use this'}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The picker itself. Fetched only when it is opened, because it is a live call to
 * Google — a list loaded on every page view would be a Google round trip a tenant
 * did not ask for.
 *
 * **Unreadable properties are shown and disabled rather than hidden.** An account
 * with `siteUnverifiedUser` on a property can see it listed and gets nothing back
 * from any query against it; hiding it would leave somebody who *knows* they own
 * that property looking at a list without it, certain the product is broken. Saying
 * *"this account can see it but not read it"* is the answer they can act on.
 */
function PropertyPicker({
  siteId,
  current,
  onChosen,
}: {
  siteId: string
  current: string | null
  onChosen: () => void
}): JSX.Element {
  const properties = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/properties', {
          params: { path: { site_id: siteId } },
          signal,
        }),
      ),
    [siteId],
  )
  const choose = useMutation((siteUrl: string) =>
    call(() =>
      api.PUT('/api/v1/seo/sites/{site_id}/property', {
        params: { path: { site_id: siteId } },
        body: { site_url: siteUrl },
      }),
    ),
  )

  if (properties.state.status === 'loading') return <Loading label="Asking Google" />
  if (properties.state.status === 'error') {
    return <ErrorState failure={properties.state.failure} onRetry={properties.refetch} />
  }

  const listed = properties.state.data.properties
  if (listed.length === 0) {
    return (
      <Empty
        title="This Google account owns no Search Console properties"
        body="Sign in with the account that owns your site, or ask whoever manages it to add you in Search Console."
        action={<Link to="/settings/connections">Connect a different account</Link>}
      />
    )
  }

  return (
    <div className="ar-panel ar-stack ar-stack--tight">
      <InlineError failure={choose.state.failure} />
      <strong>Which property is this site?</strong>
      <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
        A domain property covers every subdomain and both http and https. A URL-prefix property
        covers exactly that address — they hold different data.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {listed.map((property) => (
          <li key={property.site_url} className="app-conn">
            <span className="app-conn__body">
              <span className="ar-mono">{property.site_url}</span>
              <span className="ar-text-body-sm ar-muted">
                {property.is_domain_property ? 'Domain property' : 'URL prefix'} ·{' '}
                {property.readable
                  ? property.permission_level
                  : 'this account can see it but not read it'}
              </span>
            </span>
            <Button
              size="sm"
              tone={property.site_url === current ? 'ghost' : 'primary'}
              disabled={!property.readable || property.site_url === current}
              busy={choose.pending}
              busyLabel="Saving"
              onClick={() => {
                void (async () => {
                  const result = await choose.run(property.site_url)
                  if (result.ok) onChosen()
                })()
              }}
            >
              {property.site_url === current ? 'Current' : 'Use this'}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The four figures and the query table. Shot 5 of the demonstration video. */
function Figures({
  data,
}: {
  data: {
    clicks: number
    impressions: number
    ctr: number
    position: number
    start_date: string
    end_date: string
    rows: {
      query: string
      clicks: number
      impressions: number
      ctr: number
      position: number
    }[]
  }
}): JSX.Element {
  return (
    <>
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>
            {formatDate(data.start_date)} — {formatDate(data.end_date)}
          </h3>
        </div>
        <div className="ar-card__body">
          <div className="app-stats">
            <Stat label="Clicks" value={data.clicks.toLocaleString('en-IN')} />
            <Stat label="Impressions" value={data.impressions.toLocaleString('en-IN')} />
            <Stat label="CTR" value={`${(data.ctr * 100).toFixed(1)}%`} />
            <Stat label="Average position" value={data.position.toFixed(1)} />
          </div>
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            Straight from Google Search Console. Its data lags by two to three days, so the window
            ends earlier than today — that way the numbers do not change under you on a reload.
          </p>
        </div>
      </section>

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Top queries</h3>
        </div>
        {/*
          The table stays outside a body and full-bleed — its cells carry their
          own padding and its row hover is meant to reach both card edges — so
          only the empty state that replaces it needs the inset.
        */}
        {data.rows.length === 0 ? (
          <div className="ar-card__body">
            <p className="ar-empty ar-empty--inline">
              Google reported no individual search terms for this window, which happens on a
              property with very little traffic.
            </p>
          </div>
        ) : (
          <div className="ar-table-wrap">
            <table className="ar-table">
              <thead>
                <tr>
                  <th scope="col">Query</th>
                  <th scope="col">Clicks</th>
                  <th scope="col">Impressions</th>
                  <th scope="col">CTR</th>
                  <th scope="col">Position</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.query}>
                    <td>{row.query}</td>
                    <td>{row.clicks.toLocaleString('en-IN')}</td>
                    <td>{row.impressions.toLocaleString('en-IN')}</td>
                    <td>{(row.ctr * 100).toFixed(1)}%</td>
                    <td>{row.position.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="app-stat">
      <span className="app-stat__label">{label}</span>
      <span className="app-stat__value">{value}</span>
    </div>
  )
}
